import { cloneDeep, flattenDeep, uniqBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

const getQuestionText = (job, question) => {
  const company = job.jobCompanies.filter(c => !!c.company)
    .find(cc => question.currentVersion.clientSpecificConfigurations
      .find(c => c.companyId === cc.company.id),
    );

  if (!company) {
    return question.currentVersion.text;
  }

  const clientConfiguration = question.currentVersion.clientSpecificConfigurations.find(
    c => c.companyId === company.company.id,
  );

  if (clientConfiguration) {
    return clientConfiguration.text;
  }

  return question.currentVersion.text;
};

const nestSections = async (sections) => {
  const sectionsCopy = [...sections];
  const nestedSections = await Promise.all(sectionsCopy.map(async section => {
    const sectionHasParent = section.parentPosition !== null && !Number.isNaN(parseInt(section.parentPosition, 10));
    if (sectionHasParent) {
      const parentSection = sectionsCopy.find(sec => sec.position === section.parentPosition);
      if (parentSection) {
        if (!parentSection.childSections) {
          parentSection.childSections = [];
        }
        parentSection.childSections.push(section);
      }
    }
    return section;
  }));

  const firstIndexOfChildSections = nestedSections.findIndex(section => section.parentPosition !== null && !Number.isNaN(parseInt(section.parentPosition, 10)));

  if (firstIndexOfChildSections !== -1) {
    nestedSections.splice(firstIndexOfChildSections);
  }

  return nestedSections;
};

function sortSections(checklistSections = []) {
  if (!Array.isArray(checklistSections)) {
    return [];
  }
  const sections = [...checklistSections];
  sections.sort((a, b) => {
    const aHasParent = a.parentPosition !== null && !Number.isNaN(parseInt(a.parentPosition, 10));
    const bHasParent = b.parentPosition !== null && !Number.isNaN(parseInt(b.parentPosition, 10));
    if (!aHasParent && bHasParent) {
      return -1;
    }
    if (aHasParent && !bHasParent) {
      return 1;
    }

    return 0;

  });

  return sections;
}

const updateSectionPositionsAndNumbers = async (
  nestedSections = [],
  parentPosition = null,
  parentChecklistSectionNumber = null,
) => {
  return new Promise(resolve => {
    for (let i = 0; i < nestedSections.length; i += 1) {
      const sec = nestedSections[i];
      sec.position = i + 1;
      if (parentPosition !== null) {
        sec.parentPosition = parentPosition;
      }
      sec.checklistSectionNumber = '';
      if (parentChecklistSectionNumber !== null) {
        sec.checklistSectionNumber += `${parentChecklistSectionNumber}.`;
      }
      sec.checklistSectionNumber += `${sec.checklistSectionIdAlias || sec.position}`;

      if (sec.questions && Array.isArray(sec.questions)) {
        sec.questions = sec.questions.map((question, index) => {
          const q = { ...question };
          q.position = index + 1;
          q.questionNumber = `${sec.checklistSectionNumber}.${q.checklistQuestionIdAlias
          || q.checklistQuestion?.currentVersion?.checklistQuestionIdAlias || q.position}.`;
          return q;
        });
      }

      if (sec.childSections && sec.childSections.length > 0) {
        updateSectionPositionsAndNumbers(
          sec.childSections,
          sec.position,
          sec.checklistSectionNumber);
      }
    }
    resolve(nestedSections);
  });
};

const extractQuestions = async (processedChecklistSections = [], result = []) => {
  return Promise.all(processedChecklistSections.map(async checklistSection => {
    const { questions, childSections } = checklistSection;
    if (childSections && Array.isArray(childSections) && childSections.length > 0) {
      return extractQuestions(childSections, questions || []);
    }

    return result.concat(questions);
  }));
};

const prepareChecklistQuestions = async (job, checklists = []) => {
  const questions = await Promise.all((checklists)
    .map(async ({ id: checklistId, currentVersion }) => {
      const checklistSections = cloneDeep(currentVersion)?.checklistSections || [];
      const parentFirstChildLastSections = sortSections(checklistSections);
      const nestedSections = await nestSections(parentFirstChildLastSections);
      const sectionsWithUpdatedPositionsAndNumbers = await updateSectionPositionsAndNumbers(nestedSections);
      const extractedQuestions = await extractQuestions(sectionsWithUpdatedPositionsAndNumbers);

      return Promise.all((flattenDeep(extractedQuestions) || [])
        .map(async ({
          checklistQuestion,
          checklistQuestionIdAlias,
          position: questionPosition,
          questionNumber,
        }) => {
          const questionText = getQuestionText(job, checklistQuestion);
          const {
            id: questionId,
            currentVersion: checklistQuestionCurrentVersion,
          } = checklistQuestion;
          const questionCurrentVersionId = checklistQuestionCurrentVersion?.id;
          return {
            uniqueId: uuidv4(),
            checklistId,
            questionId,
            checklistQuestionIdAlias: checklistQuestionIdAlias || checklistQuestionCurrentVersion.checklistQuestionIdAlias,
            questionPosition,
            questionNumber,
            questionCurrentVersionId,
            text: questionText,
          };
        }));
    }));

  return uniqBy(questions.flat(3), 'questionId');
};

export default prepareChecklistQuestions;
